import closeButton from 'public/static/images/close-button-white.svg';
import ReactModal, { Props as ReactModalProps } from 'react-modal';
import css from 'styled-jsx/css';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { screen } from 'components/styles/variables';
import { useMedia } from 'react-use';

export const openModalCSS = css.resolve`
  .modal-open {
    overflow: visible;
    height: 100vh;
  }
`;

export const htmlOpenCss = css.resolve`
  overflow: hidden;
`;

/* Casting modal styles to resolve typing issue discussed in this post:
  https://github.com/cssinjs/jss/issues/1344
  Removal of babel config has caused issues in classname based styling not
  applying to the modal, requiring those styles to be passed in via ReactModal's style prop instead 
*/
function modalStyling(isDesktop: boolean): ReactModal.Styles {
  return {
    overlay: {
      overflow: 'hidden',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 10000,
      position: 'fixed',
      top: '0px',
      left: '0px',
      right: '0px',
      bottom: '0px',
      ...(isDesktop && {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }),
    },
    content: {
      padding: '0',
      border: 'none',
      overflow: 'auto',
      outline: 'none',
      zIndex: '601',
      height: '100vh',
      width: '100vw',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
  };
}

type Props = ReactModalProps & {
  children: ReactNode;
  closeButtonClassName?: string;
  overlayClassName?: string;
  withCloseButton?: boolean;
};

export default function Modal({
  className,
  children,
  bodyOpenClassName,
  onRequestClose,
  closeButtonClassName,
  overlayClassName,
  withCloseButton = true,
  ...props
}: Props): JSX.Element {
  const isDesktop = useMedia(`(min-width: ${screen.smMin})`);
  return (
    <>
      <ReactModal
        {...props}
        style={modalStyling(isDesktop)}
        className={classNames('vdp-modal', className)}
        overlayClassName={overlayClassName}
        bodyOpenClassName={classNames(openModalCSS.className, 'modal-open', bodyOpenClassName)}
        htmlOpenClassName={htmlOpenCss.className}
        onRequestClose={onRequestClose}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        {withCloseButton && (
          <button
            aria-label="close"
            className={classNames('close-button', closeButtonClassName)}
            onClick={onRequestClose}
            data-testid="close"
          />
        )}
        {children}
      </ReactModal>
      {openModalCSS.styles}
      {htmlOpenCss.styles}
      <style jsx>{`
        .close-button {
          position: absolute;
          top: 23px;
          right: 23px;
          padding: 0;
          background: none;
          border: none;
          width: 32px;
          height: 32px;
          z-index: 1;
          background-image: url(${closeButton});
          background-size: cover;
        }

        @media (min-width: ${screen.smMin}) {
          .close-button {
            top: 28px;
          }
        }

        .close-button img {
          background: none;
          border: none;
          width: 32px;
          height: 32px;
        }
      `}</style>
    </>
  );
}
